import murmurhash from 'murmurhash';

import type * as api from '@gitbook/api-client';

import type { Collection } from './collections';
import type { Space } from './spaces';

const RANDOM_FALLBACK_EMOJIS = [
    '2712',
    '1f58b',
    '1f58a',
    '270d',
    '270f',
    '1f4dd',
    '1f58c',
    '1f58d',
    '1f3a8',
    '1f4d5',
    '1f4d7',
    '1f4d8',
    '1f4d9',
    '1f4da',
    '1f4d3',
    '1f4d4',
    '1f4d2',
    '1f4d6',
    '1f4c1',
    '1f4c2',
    '1f4cb',
    '1f4c3',
    '1f4c4',
    '1f4ca',
    '1f4c8',
    '1f4c9',
    '1f50f',
    '1f4bb',
    '1f4a1',
    '1f4cc',
    '2728',
];

/**
 * Get the emoji to represent a space or collection. Falls back to an emoji based on the
 * objectID and/or title if necessary.
 */
export function getContentEmoji(content: Space | api.Space | Collection): string {
    if ('emoji' in content && content.emoji) {
        return content.emoji;
    }

    const contentKey = 'key' in content ? content.key : content.id;
    const hash = murmurhash(contentKey);
    const fallbackEmoji =
        RANDOM_FALLBACK_EMOJIS[
            // If we're in tests always use the same emoji to prevent false positives when diffing
            // screenshots
            process.env.GITBOOK_PREDICTABLE_UI ? 0 : hash % RANDOM_FALLBACK_EMOJIS.length
        ];

    return fallbackEmoji;
}

/**
 * Resolves the icon for content, depending on where its being rendered.
 */
export function resolveContentIcon(
    content: Space | api.Space | Collection
):
    | { emoji: string; iconURL: undefined }
    | { iconURL: string; emoji: undefined }
    | { iconURL: undefined; emoji: undefined } {
    if ('urls' in content && content.urls && content.urls.icon) {
        return { iconURL: content.urls.icon, emoji: undefined };
    }

    if ('iconURL' in content && content.iconURL) {
        return { iconURL: content.iconURL, emoji: undefined };
    }

    if ('emoji' in content && content.emoji) {
        return { emoji: content.emoji, iconURL: undefined };
    }

    // Otherwise rely on a generated emoji
    return {
        emoji: undefined,
        iconURL: undefined,
    };
}
